import type { DonationApi } from "../../api/DonationApi";
import type { CreateDonation } from "../../generated/dto/CreateDonation";
import type { CreateDonationFundStaff } from "../../generated/dto/CreateDonationFundStaff";
import type { CreateDonationInitiative } from "../../generated/dto/CreateDonationInitiative";
import type { CreateRecurringDonation } from "../../generated/dto/CreateRecurringDonation";
import type { CsvExportOptions } from "../../generated/dto/CsvExportOptions";
import type { DeleteDonationFundStaff } from "../../generated/dto/DeleteDonationFundStaff";
import type { DeleteDonationInitiativeStaff } from "../../generated/dto/DeleteDonationInitiativeStaff";
import type { DeleteDonationStaff } from "../../generated/dto/DeleteDonationStaff";
import type { DeleteRecurringDonationStaff } from "../../generated/dto/DeleteRecurringDonationStaff";
import type { DonationFetchOptions } from "../../generated/dto/DonationFetchOptions";
import type { DonationFundStaff } from "../../generated/dto/DonationFundStaff";
import type { DonationFundStaffSearchParams } from "../../generated/dto/DonationFundStaffSearchParams";
import type { DonationFundStaffSearchResult } from "../../generated/dto/DonationFundStaffSearchResult";
import type { DonationInitiativeSearchParams } from "../../generated/dto/DonationInitiativeSearchParams";
import type { DonationInitiativeStaff } from "../../generated/dto/DonationInitiativeStaff";
import type { DonationInitiativeStaffSearchResult } from "../../generated/dto/DonationInitiativeStaffSearchResult";
import type { DonationMember } from "../../generated/dto/DonationMember";
import type { DonationMemberFetchOptions } from "../../generated/dto/DonationMemberFetchOptions";
import type { DonationMemberSearchParams } from "../../generated/dto/DonationMemberSearchParams";
import type { DonationMemberSearchResult } from "../../generated/dto/DonationMemberSearchResult";
import type { DonationSearchParams } from "../../generated/dto/DonationSearchParams";
import type { DonationStaff } from "../../generated/dto/DonationStaff";
import type { DonationStaffSearchResult } from "../../generated/dto/DonationStaffSearchResult";
import type { MemberDonationOptions } from "../../generated/dto/MemberDonationOptions";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { RecurringDonationStaff } from "../../generated/dto/RecurringDonationStaff";
import type { UpdateDonation } from "../../generated/dto/UpdateDonation";
import type { UpdateDonationFundStaff } from "../../generated/dto/UpdateDonationFundStaff";
import type { UpdateDonationInitiative } from "../../generated/dto/UpdateDonationInitiative";
import type { UpdateRecurringDonation } from "../../generated/dto/UpdateRecurringDonation";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class DonationFrontendService {
  constructor(private readonly api: DonationApi) {}
  private readonly donationFundStaffCache: Map<number, DonationFundStaff> = new Map();
  private readonly donationInitiativeStaffCache: Map<number, DonationInitiativeStaff> = new Map();
  async getDonationMember(id: number, options?: DonationMemberFetchOptions): Promise<DonationMember> {
    return this.api.getDonationMember(id, options)
  }
  async getDonationStaff(id: number, options?: DonationFetchOptions): Promise<DonationStaff> {
    return this.api.getDonationStaff(id, options)
  }
  async getRecurringDonationStaff(id: number): Promise<RecurringDonationStaff> {
    return this.api.getRecurringDonationStaff(id)
  }
  async getDonationFundStaff(id: number): Promise<DonationFundStaff> {
    const __cached = this.donationFundStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getDonationFundStaff(id);
    this.donationFundStaffCache.set(id, __value);
    return __value
  }
  async getDonationInitiativeStaff(id: number): Promise<DonationInitiativeStaff> {
    const __cached = this.donationInitiativeStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getDonationInitiativeStaff(id);
    this.donationInitiativeStaffCache.set(id, __value);
    return __value
  }
  async getAllDonationFundStaffs(): Promise<Array<DonationFundStaff>> {
    return this.api.getAllDonationFundStaffs()
  }
  async getAllDonationInitiativeStaffs(): Promise<Array<DonationInitiativeStaff>> {
    return this.api.getAllDonationInitiativeStaffs()
  }
  async createDonationStaff(source: CreateDonation): Promise<DonationStaff> {
    return this.api.createDonationStaff(source)
  }
  async createRecurringDonationStaff(source: CreateRecurringDonation): Promise<RecurringDonationStaff> {
    return this.api.createRecurringDonationStaff(source)
  }
  async createDonationFundStaff(source: CreateDonationFundStaff): Promise<DonationFundStaff> {
    const __result = await this.api.createDonationFundStaff(source);
    this.donationFundStaffCache.clear();
    return __result
  }
  async createDonationInitiativeStaff(source: CreateDonationInitiative): Promise<DonationInitiativeStaff> {
    const __result = await this.api.createDonationInitiativeStaff(source);
    this.donationInitiativeStaffCache.clear();
    return __result
  }
  async updateDonationStaff(source: UpdateDonation): Promise<DonationStaff> {
    return this.api.updateDonationStaff(source)
  }
  async updateRecurringDonationStaff(source: UpdateRecurringDonation): Promise<RecurringDonationStaff> {
    return this.api.updateRecurringDonationStaff(source)
  }
  async updateDonationFundStaff(source: UpdateDonationFundStaff): Promise<DonationFundStaff> {
    const __result = await this.api.updateDonationFundStaff(source);
    this.donationFundStaffCache.clear();
    return __result
  }
  async updateDonationInitiativeStaff(source: UpdateDonationInitiative): Promise<DonationInitiativeStaff> {
    const __result = await this.api.updateDonationInitiativeStaff(source);
    this.donationInitiativeStaffCache.clear();
    return __result
  }
  async deleteDonationStaff(source: DeleteDonationStaff): Promise<void> {
    return this.api.deleteDonationStaff(source)
  }
  async deleteRecurringDonationStaff(source: DeleteRecurringDonationStaff): Promise<void> {
    return this.api.deleteRecurringDonationStaff(source)
  }
  async deleteDonationFundStaff(source: DeleteDonationFundStaff): Promise<void> {
    const __result = await this.api.deleteDonationFundStaff(source);
    this.donationFundStaffCache.clear();
    return __result
  }
  async deleteDonationInitiativeStaff(source: DeleteDonationInitiativeStaff): Promise<void> {
    const __result = await this.api.deleteDonationInitiativeStaff(source);
    this.donationInitiativeStaffCache.clear();
    return __result
  }
  async searchDonationMember(params: DonationMemberSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationMemberSearchResult>> {
    return this.api.searchDonationMember(params, page, perPage)
  }
  async searchDonationStaff(params: DonationSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationStaffSearchResult>> {
    return this.api.searchDonationStaff(params, page, perPage)
  }
  async exportDonationStaffSearchToCsv(params: DonationSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportDonationStaffSearchToCsv(params, options)
  }
  async searchDonationFundStaff(params: DonationFundStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationFundStaffSearchResult>> {
    return this.api.searchDonationFundStaff(params, page, perPage)
  }
  async searchDonationInitiativeStaff(params: DonationInitiativeSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationInitiativeStaffSearchResult>> {
    return this.api.searchDonationInitiativeStaff(params, page, perPage)
  }
  async searchDonationMemberFull(params: DonationMemberSearchParams, page: number, perPage: number, options?: DonationMemberFetchOptions): Promise<PaginationResult<DonationMember>> {
    return this.api.searchDonationMemberFull(params, page, perPage, options)
  }
  async searchDonationStaffFull(params: DonationSearchParams, page: number, perPage: number, options?: DonationFetchOptions): Promise<PaginationResult<DonationStaff>> {
    return this.api.searchDonationStaffFull(params, page, perPage, options)
  }
  async searchDonationFundStaffFull(params: DonationFundStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationFundStaff>> {
    return this.api.searchDonationFundStaffFull(params, page, perPage)
  }
  async searchDonationInitiativeStaffFull(params: DonationInitiativeSearchParams, page: number, perPage: number): Promise<PaginationResult<DonationInitiativeStaff>> {
    return this.api.searchDonationInitiativeStaffFull(params, page, perPage)
  }
  async getDonationStaffFundId(id: number): Promise<DonationFundStaff> {
    return this.api.getDonationStaffFundId(id)
  }
  async getDonationStaffInitiativeId(id: number): Promise<DonationInitiativeStaff> {
    return this.api.getDonationStaffInitiativeId(id)
  }
  async getDonationStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getDonationStaffPersonId(id, options)
  }
  async purchaseOptionsMember(): Promise<MemberDonationOptions> {
    return this.api.purchaseOptionsMember()
  }
  async purchaseOptionsPublic(): Promise<MemberDonationOptions> {
    return this.api.purchaseOptionsPublic()
  }
}
